import {
	type CellOutcome,
	type Comparison,
	DEFAULT_LEVEL,
	formatDuration,
	type GameState,
	type Level,
	levelName,
	type PuzzleModel,
	shareSummary,
} from '@arrow/game-engine';
import type { PuzzleType } from '@arrow/puzzle-schema';
import { type CSSProperties, useEffect, useId, useMemo, useRef, useState } from 'react';

import { Link, useOverlay } from '../app/router.tsx';
import { settings } from '../app/settings.ts';
import { type ChallengeContext, verdict } from '../game/challenge.ts';
import type { ShareOutcome } from '../game/share.ts';
import { percentileFor, shareSolve, type SolveForShare } from '../game/share-flow.ts';
import type { ShareVariant } from '../platform/analytics.ts';
import { prepareMyChallenge } from '../platform/challenges.ts';
import { BackPill, type BackTo } from './BackPill.tsx';
import { BoardMap } from './BoardMap.tsx';
import { busyButton } from './busy.ts';
import { DartMark } from './DartMark.tsx';
import { LimeTick, Swords } from './icons.tsx';
import { Nib } from './Nib.tsx';
import { PosterNestLine, usePosterNest } from './PosterNestLine.tsx';
import { ResultMoment } from '../push/Moments.tsx';
import { resultMoment } from '../push/state.ts';
import { Modal, useModalBackground, useReturnFocus } from '../ui/index.ts';

export interface ResultData {
	number: number | null;
	title: string;
	dateKey: string | null;
	game: GameState;
	bestFlow: number;
	streak: number;
	/** Null when there's nothing to compare (not a daily, or yesterday wasn't played). */
	comparison: Comparison | null;
	/**
	 * Every Flow chain as answer ids, in solve order (`flow.segments`). The poster draws the
	 * longest one in cobalt on the board map. Optional: without it, no route.
	 */
	segments?: readonly (readonly string[])[];
	/** The daily's level (or the practice puzzle's): "Thursday · Hard", and in the share. */
	level?: Level | undefined;
	/** The puzzle's type (a Halloween puzzle's Nib wears his hat). */
	type?: PuzzleType | undefined;
	/** What was played, for the share link and `share_pressed`. */
	kind?: 'daily' | 'practice' | 'puzzle' | undefined;
	/** Practice: the share link opens `/play/<id>`. */
	puzzleId?: string | undefined;
	/** How each cell fell (`solveOutcomes`), for the share's emoji board. */
	outcomes?: readonly CellOutcome[] | undefined;
	/** The player's local date (a challenge remembers the day it was shared). */
	todayKey?: string | undefined;
	/** Played from a challenge: the poster says who won and offers the rematch. */
	challenge?: ChallengeContext | null | undefined;
}

/** A finished solve, as the share flow reads it (with the player's share setting). */
export function solveForShare(data: ResultData, width: number): SolveForShare {
	return {
		kind: data.kind ?? 'puzzle',
		number: data.number,
		level: data.level,
		puzzleId: data.puzzleId,
		elapsedMs: data.game.elapsedMs,
		bestFlow: data.bestFlow,
		checks: data.game.checksUsed,
		reveals: data.game.revealsUsed,
		outcomes: data.outcomes,
		width,
		board: settings().get().shareBoard,
		challenge: data.challenge ?? null,
	};
}

/**
 * Share the solve as `variant` (the poster's buttons, and the finished board's one).
 * Challenge and rematch first make this device's (anonymous) challenge. Never throws.
 */
export async function shareFromResult(data: ResultData, width: number, variant: ShareVariant): Promise<ShareOutcome> {
	return (await shareFromResultDetail(data, width, variant)).outcome;
}

/** As `shareFromResult`, and whether a dare went without a time to beat (`plain`, UX-045). */
export async function shareFromResultDetail(
	data: ResultData,
	width: number,
	variant: ShareVariant,
): Promise<{ outcome: ShareOutcome; plain: boolean }> {
	const { method, plain } = await shareSolve(solveForShare(data, width), variant, { todayKey: data.todayKey ?? data.dateKey ?? '' });
	return { outcome: method === 'native' ? 'shared' : method === 'clipboard' ? 'copied' : method, plain };
}

/**
 * What the poster's status line says after a share: nothing for a native share that went,
 * "Copied to the clipboard", or, when a dare went without its challenge link, that it has
 * no time to beat and why.
 */
export function shareStatus(outcome: ShareOutcome | null, plain: boolean, online = typeof navigator === 'undefined' || navigator.onLine !== false): string | null {
	// Each fits the finished board's one-line slip at 320 (QA3-3, QA3-7).
	if (outcome === 'failed') return 'Couldn’t share here. Try a screenshot.';
	if (outcome !== 'shared' && outcome !== 'copied') return null;
	if (plain) return `${outcome === 'copied' ? 'Copied' : 'Shared'} without a time to beat. ${online ? 'Try again.' : 'You’re offline.'}`;
	return outcome === 'copied' ? 'Copied to the clipboard' : null;
}

/**
 * Which share a finished solve leads with: the dare, the rematch, or (practice) the result.
 * A challenge opened on a puzzle you'd already solved is settled: both of you have played
 * it, so a rematch would be a loop with no game in it (QA2-2). It leads with the plain
 * share, and the poster's way on offers what's left today.
 */
export function primaryVariant(data: Pick<ResultData, 'kind' | 'number' | 'challenge'>): ShareVariant {
	if (data.kind !== 'daily' || !data.number) return 'result';
	if (data.challenge?.settled) return 'result';
	return data.challenge ? 'rematch' : 'challenge';
}

/** The poster's way on, below its crop line (never inside the screenshot area). */
export interface WayOn {
	/** "‹ Today", or "‹ Play" after a practice puzzle or a game opened from Play. */
	back: BackTo;
	/**
	 * Where on: today's daily with levels left ("Hard is waiting, 8×8." · Hard ›), or a game
	 * from Play, the next in its stack or search ("Next in Missed" · Fri’s Hard ›).
	 */
	next: { lead: string; label: string; ariaLabel: string; onGo: () => void } | null;
}

interface Props {
	open: boolean;
	data: ResultData;
	onClose: () => void;
	model: PuzzleModel;
	wayOn?: WayOn | undefined;
}

/**
 * The finish: a full-page poster on paper (not a card on a scrim), set as one centred
 * column. The Dart + "sparrow" wordmark (so a screenshot is recognisably Sparrow), a huge DONE., the date, the board lifted bare as a big map in the middle (the
 * best Flow chain drawn on it in cobalt, its only picture), then the time with the Flow
 * count beside it and happy Nib (eyes shut, wing up). Below the crop line, the foot in three groups
 * (J6): the share in one fixed slot, the streak on its own line, and where next in one
 * row (‹ Today · See the board · the next level, or More in Play). Sentence case throughout, except DONE.
 * Built to be screenshotted, so nothing on it spoils an answer. A modal over the whole page:
 * portalled to <body>, the page inert behind it, focus in on DONE. and back out to its opener.
 */
export function ResultSheet({ open, data, onClose, model, wayOn }: Props) {
	const { game } = data;
	const rootRef = useRef<HTMLElement>(null);
	const headingRef = useRef<HTMLHeadingElement>(null);
	const [shared, setShared] = useState<{ outcome: ShareOutcome; plain: boolean } | null>(null);
	const [busy, setBusy] = useState<ShareVariant | null>(null);
	const daily = data.kind === 'daily' && !!data.number;
	const primary = primaryVariant(data);
	const vs = data.challenge ? verdict(game.elapsedMs, data.challenge.durationMs) : null;
	// In a nest (behind the nests gate): one line with your place, and the poster keeps
	// "Share result" rather than the dare (docs/mockups/nest-prototype.html, the poster).
	const nestPoster = usePosterNest(
		daily && data.level && data.dateKey && !data.challenge ? { number: data.number!, level: data.level, dateKey: data.dateKey } : null,
		open,
	);
	const dare = daily && !nestPoster.nest && primary !== 'result';

	// The poster owns a history entry while it's up: back closes it onto the board.
	useOverlay(open, onClose);
	// Mounted as it opens (result.css fades it in) and kept through the fade out.
	const mounted = useMounted(open);

	// A modal over the whole page (UX-018): the game behind it is inert while it's up. Declared
	// before the focus effect, so the page is live again by the time focus goes back into it.
	useModalBackground(open);
	// …and on close, focus goes back to what opened it: the grid after a solve, "See result"
	// from the foot (or the board, if that's gone).
	useReturnFocus(open, () => document.querySelector<HTMLElement>('[data-game-surface]'));

	// Ask for the percentile as the poster opens, so the share finds it ready. The dare's code
	// too (UX-045), so the tap only formats and shares; but only when "Count my plays" is on,
	// because with it off a solve leaves the device only when the player asks for a challenge.
	useEffect(() => {
		if (!open || !daily) return;
		const level = data.level ?? DEFAULT_LEVEL;
		void percentileFor(data.number!, level, game.elapsedMs);
		if (dare && settings().get().analytics) void prepareMyChallenge({ number: data.number!, level });
	}, [open, daily, dare, data.number, data.level, game.elapsedMs]);

	// Focus goes to DONE. once the poster can take it (UX-044): at once, again on the next frame
	// and when its fade ends, since the grid underneath may still hold focus as it opens.
	useEffect(() => {
		if (!open) {
			setShared(null);
			return;
		}
		const root = rootRef.current;
		const take = () => {
			const here = document.activeElement;
			if (!(here instanceof Node && root?.contains(here))) headingRef.current?.focus({ preventScroll: true });
		};
		take();
		const frame = requestAnimationFrame(take);
		root?.addEventListener('transitionend', take, { once: true });
		return () => {
			cancelAnimationFrame(frame);
			root?.removeEventListener('transitionend', take);
		};
	}, [open]);


	const elapsed = formatDuration(game.elapsedMs);
	const perfect = game.checksUsed === 0 && game.revealsUsed === 0;
	const faster = data.comparison?.fasterThanYesterday === true && data.comparison.deltaMs !== null;
	// A single answer isn't a Flow: below ×2 the poster leaves Flow out.
	const flow = data.bestFlow >= 2 ? `×${data.bestFlow}` : null;
	const chain = useMemo(() => longest(data.segments ?? []), [data.segments]);
	const day = data.dateKey ? parseDateKey(data.dateKey) : null;
	// "Thursday · Hard" in the brand italic, then "22 October, No. 42"; practice puzzles
	// set their title instead of the day.
	const lv = data.level ? levelName(data.level) : null;
	const dayName = day ? day.toLocaleDateString('en-GB', { weekday: 'long' }) : data.title;
	const dateText = lv ? `${dayName} · ${lv}` : dayName;
	const when = day ? day.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : null;
	const meta = [when, data.number !== null ? `No. ${data.number}` : null].filter(Boolean).join(', ');
	const label = data.number !== null ? `Sparrow number ${data.number}${lv ? `, ${lv}` : ''}` : `Sparrow: ${data.title}`;
	// The map's box: its aspect, and its column count (a cap on the cell size, for small boards).
	const mapVars = { '--map-ar': model.width / model.height, '--map-cols': model.width } as CSSProperties;

	const onShare = async (variant: ShareVariant) => {
		if (busy) return;
		setBusy(variant);
		setShared(null);
		try {
			setShared(await shareFromResultDetail(data, model.width, variant));
		} finally {
			setBusy(null);
		}
	};

	const status = shared ? shareStatus(shared.outcome, shared.plain) : null;
	// The streak shows on a dated puzzle (a daily), as before.
	const streak = data.dateKey && data.streak > 0 ? data.streak : 0;
	const leadId = useId();
	// Notifications' moment, below the crop line: today's daily, never a challenge's result.
	const moment = resultMoment({ kind: data.kind, dateKey: data.dateKey, todayKey: data.todayKey, challenge: !!data.challenge, streak: data.streak });

	const poster = (
		<section
			ref={rootRef}
			className={open ? 'result show' : 'result'}
			role="dialog"
			aria-modal="true"
			aria-labelledby="result-title"
			aria-describedby="result-summary"
			aria-hidden={!open}
			inert={!open}
		>
			<div className="poster">
				{/* Two columns on a phone held sideways (result.css, QA4-5): DONE. over the board,
				    then the solve and its shares beside them. One column otherwise. */}
				<div className="p-lead">
					<div className="p-title">
						<p className="p-brand" aria-hidden="true">
							<DartMark className="p-brand-mark" />
							<span className="p-wm">sparrow</span>
						</p>
						<h2 id="result-title" className="p-head" tabIndex={-1} ref={headingRef}>
							Done.<span className="sr-only"> {label}</span>
						</h2>
						{/* The solve in one line of words for assistive tech; the picture board and the
						    stats block below are hidden from it (no "multiplication sign", no squares). */}
						<p id="result-summary" className="sr-only">
							{shareSummary({ elapsedMs: game.elapsedMs, bestFlow: data.bestFlow, checks: game.checksUsed, reveals: game.revealsUsed })}
						</p>
						<p className="p-date">
							<span className="p-day">{dateText}</span>
							{meta && (
								<>
									{' '}
									<span className="p-no">{meta}</span>
								</>
							)}
						</p>
					</div>

					{/* The picture: hidden from assistive tech, which has the summary line above. */}
					<div className="p-map" data-poster-map="" style={mapVars} aria-hidden="true">
						<BoardMap model={model} chain={chain} revealed={game.revealedAnswers} animate={open} />
					</div>
				</div>

				<div className="p-side">
					<div className="p-stats" aria-hidden="true">
						<div className="p-time" data-len={Math.min(7, elapsed.length)}>
							{elapsed}
						</div>
						{/* The cobalt chain on the map is the one picture of the best Flow; this is its count. */}
						<div className="p-sum">
							{flow && (
								<>
									<div className="p-fx" data-len={flow.length - 1}>
										{flow}
									</div>
									<span className="p-fl">Best Flow</span>
								</>
							)}
							{perfect ? (
								<span className="p-use p-perfect">
									{/* Lime = "yes", always beside the words, never colour alone. */}
									<Tick />
									Perfect solve
								</span>
							) : (
								usage(game).map((line) => (
									<span key={line.text} className={line.reveals ? 'p-use rv' : 'p-use'}>
										{line.text}
									</span>
								))
							)}
						</div>
						{/* Nib, pleased: the poster's one illustration, beside the time. */}
						<Nib pose="happy" className="p-nib" type={data.type} />
					</div>
					{faster && <p className="p-faster">{formatDuration(-(data.comparison!.deltaMs ?? 0))} faster than yesterday</p>}

					{vs && (
						<p className={vs.beat || vs.tie ? 'p-vs won' : 'p-vs lost'} data-testid="verdict">
							{vs.beat && <LimeTick className="p-vs-tick" />}
							{vs.text}
						</p>
					)}

					{nestPoster.line && <PosterNestLine line={nestPoster.line} />}

					{/* J6 (option A): three groups. The share in one fixed slot, the same place on every
					    poster (red for the dare or rematch, ink for a plain share) with "Share result
					    only" beneath the dare; the status on one line; then where next, one row. */}
					<div className="p-shr">
						{dare ? (
							<button
								type="button"
								className="btn md dare p-dare"
								{...busyButton(busy !== null, () => void onShare(primary))}
								aria-busy={busy === primary || undefined}
							>
								<Swords />
								{busy === primary ? 'Making your challenge…' : primary === 'rematch' ? 'Send the rematch' : 'Challenge a friend'}
							</button>
						) : (
							<button
								type="button"
								className="btn md primary p-share"
								{...busyButton(busy !== null, () => void onShare('result'))}
								aria-busy={busy === 'result' || undefined}
							>
								Share result
								<Wedge dir="ur" />
							</button>
						)}
						{dare && (
							<button
								type="button"
								className="p-only"
								{...busyButton(busy !== null, () => void onShare('result'))}
								aria-busy={busy === 'result' || undefined}
							>
								Share result only
							</button>
						)}
					</div>

					{/* The streak, and a share's status beside it (the live region, UX-043). */}
					<div className="p-status-line" data-empty={(!streak && !status) || undefined}>
						{streak > 0 && (
							<span className="p-streak">
								<Flame />
								{streak}
								<small>{streak === 1 ? ' day in a row' : ' days in a row'}</small>
							</span>
						)}
						<span className="p-note" data-sep={(streak > 0 && !!status) || undefined}>
							<span className="p-status" aria-live="polite">
								{status ?? ''}
							</span>
						</span>
					</div>

					{/* Where next: home on the left, the board in the middle, the next puzzle (or, with
					    the day done, the rest of Play) on the right. */}
					<nav className="p-way" aria-label="Where next" data-testid="poster-way">
						{/* "Medium and Hard left today.": the next pill's description, not printed and not
						    read again on its own (a hidden node still describes, J6). */}
						{wayOn?.next?.lead && (
							<span id={leadId} hidden>
								{wayOn.next.lead}
							</span>
						)}
						{wayOn ? <BackPill to={wayOn.back} /> : <span className="p-way-end" aria-hidden="true" />}
						<button type="button" className="p-back" onClick={onClose}>
							See the board
						</button>
						{wayOn?.next ? (
							<button
								type="button"
								className="btn back p-go"
								onClick={wayOn.next.onGo}
								aria-label={wayOn.next.ariaLabel}
								aria-describedby={wayOn.next.lead ? leadId : undefined}
							>
								{wayOn.next.label}
								<Wedge dir="r" />
							</button>
						) : wayOn && !wayOn.back.startsWith('/play') ? (
							<Link to="/play" how="up" className="btn back p-go">
								More in Play
								<Wedge dir="r" />
							</Link>
						) : (
							<span className="p-way-end" aria-hidden="true" />
						)}
					</nav>
					{open && moment && <ResultMoment kind={moment} streak={data.streak} />}
				</div>
			</div>
		</section>
	);
	if (!mounted) return null;
	// ui/Modal (Radix Dialog) traps Tab in it and closes it on Escape. Portalled out of the
	// page (#root), so the page can go inert behind it.
	return (
		<Modal open={open} onClose={onClose} onOpenFocus={() => headingRef.current?.focus({ preventScroll: true })}>
			{poster}
		</Modal>
	);
}

/** The poster's fade out (result.css: 0.3s, 0.2s with reduced motion), before it unmounts. */
const FADE_OUT_MS = 300;

/** True while `open`, and for the fade out after. */
function useMounted(open: boolean): boolean {
	const [mounted, setMounted] = useState(open);
	if (open && !mounted) setMounted(true);
	useEffect(() => {
		if (open || !mounted) return;
		const t = window.setTimeout(() => setMounted(false), FADE_OUT_MS);
		return () => window.clearTimeout(t);
	}, [open, mounted]);
	return mounted;
}

/** The streak's flame: vermilion with an ink keyline, printed like the rest (not an emoji). */
function Flame() {
	return (
		<svg className="p-flame" viewBox="0 0 24 24" aria-hidden="true">
			<path d="M12 2c1 4 6 6 6 12a6 6 0 0 1-12 0c0-3 2-5 3-6 0 2 1 3 2 3 0-4-1-6 1-9z" />
		</svg>
	);
}

/** The perfect-solve tick: lime with an ink keyline, printed like the flame. */
function Tick() {
	return (
		<svg className="p-tick" viewBox="0 0 24 24" aria-hidden="true">
			<path d="M2.5 13.2 6.4 9.4l3.7 3.7 7.6-8.6 3.8 3.6L10.2 20z" />
		</svg>
	);
}

/** The Letterpress wedge arrow (24×24, pointing right), turned for the other directions. */
function Wedge({ dir }: { dir: 'r' | 'ur' }) {
	const rotate = dir === 'ur' ? -45 : 0;
	return (
		<svg className="wedge" viewBox="0 0 24 24" aria-hidden="true">
			<path d="M2 9.2h10.6V3.6L22 12l-9.4 8.4v-5.6H2z" transform={rotate ? `rotate(${rotate} 12 12)` : undefined} />
		</svg>
	);
}

/** "Hard left today, 10×10." / "Medium and Hard left today." */
export function waiting(levels: readonly Level[], size: string): string {
	const names = levels.map(levelName);
	if (names.length === 1) return `${names[0]} left today, ${size}.`;
	return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} left today.`;
}

function longest<T>(segments: readonly (readonly T[])[]): readonly T[] {
	let best: readonly T[] = [];
	for (const s of segments) if (s.length > best.length) best = s;
	return best;
}

/** "2 checks", "4 reveals": the reveals line is red, matching the revealed answers on the map. */
function usage(game: GameState): { text: string; reveals: boolean }[] {
	const parts: { text: string; reveals: boolean }[] = [];
	if (game.checksUsed) parts.push({ text: `${game.checksUsed} ${game.checksUsed === 1 ? 'check' : 'checks'}`, reveals: false });
	if (game.revealsUsed) parts.push({ text: `${game.revealsUsed} ${game.revealsUsed === 1 ? 'reveal' : 'reveals'}`, reveals: true });
	return parts;
}

/** A YYYY-MM-DD key as a local calendar date (never a UTC slice). */
function parseDateKey(key: string): Date | null {
	const [y, m, d] = key.split('-').map(Number);
	return y && m && d ? new Date(y, m - 1, d) : null;
}
